import type { PropertyDto } from '../../services/api';
import { propertyPublicPagesApi, type PropertyPublicPageDto } from '../../services/property-public-pages.api';
import { onLocaleChange, t, translatePropertyStatus, translatePropertyType } from '../../services/i18n';
import { toastService } from '../../services/toast';
import { PROPERTY_ICONS, escapeHtml } from './property-shared';

function toLocalDateTimeInput(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const offsetMs = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function toUtcIsoString(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function formatDateTime(value?: string | null): string {
  if (!value) return t('property.publicPage.alwaysAvailable');

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat(document.documentElement.lang || 'nl-BE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export class RcPropertyPublicPageTab extends HTMLElement {
  static get observedAttributes(): string[] {
    return ['hidden'];
  }

  propertyId = '';
  property: PropertyDto | null = null;

  private publicPage: PropertyPublicPageDto | null = null;
  private loadedPropertyId = '';
  private loading = true;
  private saving = false;
  private error = '';
  private copyFeedback = '';
  private draftIsActive = false;
  private draftExpiresAt = '';
  private disposeLocaleChange?: () => void;

  connectedCallback(): void {
    this.disposeLocaleChange = onLocaleChange(() => this.render());
    this.addEventListener('click', this.handleClick);
    this.addEventListener('change', this.handleChange as EventListener);
    if (this.shouldLoadData()) {
      void this.load();
    } else {
      this.loading = false;
      this.render();
    }
  }

  disconnectedCallback(): void {
    this.disposeLocaleChange?.();
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('change', this.handleChange as EventListener);
  }

  attributeChangedCallback(name: string, _oldValue: string | null, newValue: string | null): void {
    if (name !== 'hidden' || newValue !== null || !this.isConnected) {
      return;
    }

    if (this.shouldLoadData() && this.loadedPropertyId !== this.propertyId) {
      void this.load();
      return;
    }

    this.render();
  }

  async load(): Promise<void> {
    this.loading = true;
    this.error = '';
    this.render();

    try {
      const publicPage = await propertyPublicPagesApi.getByProperty(this.propertyId);
      this.setDraft(publicPage);
      this.publicPage = publicPage;
      this.loadedPropertyId = this.propertyId;
    } catch (error) {
      console.error('Failed to load property public page:', error);
      this.error = t('property.publicPage.loadError');
    } finally {
      this.loading = false;
      this.render();
    }
  }

  render(): void {
    if (!this.loading && this.shouldLoadData() && this.loadedPropertyId !== this.propertyId) {
      void this.load();
      this.innerHTML = `<div class="accordion-feedback">${t('common.loading')}</div>`;
      return;
    }

    if (this.loading) {
      this.innerHTML = `<div class="accordion-feedback">${t('common.loading')}</div>`;
      return;
    }

    if (this.error) {
      this.innerHTML = `<div class="empty-state">${escapeHtml(this.error)}</div>`;
      return;
    }

    const publicPage = this.publicPage;
    const status = this.getStatus();
    const linkBox = publicPage?.publicUrl
      ? `<div class="property-public-link-box">
          <span class="property-public-link-label">${t('property.publicPage.publicLink')}</span>
          <div class="property-public-link-value">
            <code>${escapeHtml(publicPage.publicUrl)}</code>
            <button
              type="button"
              class="btn-icon btn-icon-primary"
              data-action="copy-link"
              title="${t('common.copy')}"
              aria-label="${t('common.copy')}"
            >
              ${PROPERTY_ICONS.copy}
            </button>
          </div>
        </div>`
      : `<p class="property-public-link-placeholder">${t('property.publicPage.linkUnavailable')}</p>`;

    const previewCards = this.property
      ? `
        <div class="property-public-preview-grid">
          <div class="property-public-preview-card">
            <span class="property-public-preview-label">${t('property.publicPage.previewAddress')}</span>
            <strong>${escapeHtml(this.property.address)}</strong>
          </div>
          <div class="property-public-preview-card">
            <span class="property-public-preview-label">${t('property.publicPage.previewType')}</span>
            <strong>${escapeHtml(translatePropertyType(this.property.type))}</strong>
          </div>
          <div class="property-public-preview-card">
            <span class="property-public-preview-label">${t('property.publicPage.previewStatus')}</span>
            <strong>${escapeHtml(translatePropertyStatus(this.property.status))}</strong>
          </div>
          <div class="property-public-preview-card">
            <span class="property-public-preview-label">${t('property.publicPage.previewPhotos')}</span>
            <strong>${this.property.galleryPhotos?.length ?? 0}</strong>
          </div>
        </div>`
      : '';

    this.innerHTML = `
      <div class="property-public-panel">
        <div class="property-public-summary">
          <div>
            <div class="property-public-toolbar">
              <span class="badge ${status.badgeClass}">${escapeHtml(status.label)}</span>
              ${publicPage?.updatedAt ? `<span class="property-public-updated">${t('property.publicPage.updatedAt')}: ${escapeHtml(formatDateTime(publicPage.updatedAt))}</span>` : ''}
            </div>
            <h3>${t('property.publicPage.title')}</h3>
            <p>${t('property.publicPage.helper')}</p>
          </div>
          <div class="property-public-meta-card">
            <span class="property-public-preview-label">${t('property.publicPage.expiresAt')}</span>
            <strong>${escapeHtml(formatDateTime(publicPage?.expiresAtUtc))}</strong>
            <span>${t('property.publicPage.expiresAtHint')}</span>
          </div>
        </div>

        ${linkBox}

        ${this.copyFeedback ? `<p class="welcome-copy-feedback">${escapeHtml(this.copyFeedback)}</p>` : ''}

        <div class="property-public-form-grid">
          <label class="property-public-toggle">
            <input type="checkbox" data-field="isActive" ${this.draftIsActive ? 'checked' : ''}>
            <div>
              <strong>${t('property.publicPage.isActiveLabel')}</strong>
              <span>${t('property.publicPage.isActiveHint')}</span>
            </div>
          </label>

          <div class="form-group">
            <label for="property-public-expiry">${t('property.publicPage.expiresAt')}</label>
            <div class="property-public-expiry-row">
              <input id="property-public-expiry" type="datetime-local" data-field="expiresAt" value="${escapeHtml(this.draftExpiresAt)}">
              <button
                type="button"
                class="btn btn-outline btn-sm"
                data-action="clear-expiry"
                ${this.draftExpiresAt ? '' : 'disabled'}
              >
                ${t('property.publicPage.clearExpiry')}
              </button>
            </div>
            <small class="form-help">${t('property.publicPage.expiresAtHint')}</small>
          </div>
        </div>

        <div class="property-public-save-row">
          <button class="btn btn-primary" data-action="save" ${this.saving ? 'disabled' : ''}>
            ${this.saving ? t('common.saving') : t('property.publicPage.save')}
          </button>
        </div>

        <div class="property-public-preview">
          <div class="property-public-preview-header">
            <h4>${t('property.publicPage.previewTitle')}</h4>
            <p>${t('property.publicPage.previewDescription')}</p>
          </div>
          ${previewCards}
          ${this.property?.description
            ? `<div class="property-public-description-preview">${escapeHtml(this.property.description)}</div>`
            : `<p class="property-public-link-placeholder">${t('property.publicPage.previewNoDescription')}</p>`}
        </div>
      </div>
    `;
  }

  private handleClick = (event: Event): void => {
    const trigger = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-action]');
    if (!trigger) return;

    switch (trigger.dataset.action) {
      case 'save':
        void this.save();
        break;
      case 'copy-link':
        void this.copyLink();
        break;
      case 'clear-expiry':
        this.draftExpiresAt = '';
        this.render();
        break;
      default:
        break;
    }
  };

  private handleChange = (event: Event): void => {
    const target = event.target as HTMLInputElement | null;
    if (!target?.dataset.field) return;

    if (target.dataset.field === 'isActive') {
      this.draftIsActive = target.checked;
      return;
    }

    if (target.dataset.field === 'expiresAt') {
      this.draftExpiresAt = target.value;
    }
  };

  private async save(): Promise<void> {
    if (this.saving) return;

    this.saving = true;
    this.copyFeedback = '';
    this.render();

    try {
      const saved = await propertyPublicPagesApi.save(this.propertyId, {
        isActive: this.draftIsActive,
        expiresAtUtc: toUtcIsoString(this.draftExpiresAt),
      });

      this.publicPage = saved;
      this.setDraft(saved);
      toastService.success(t('property.publicPage.saved'));
    } catch (error) {
      console.error('Failed to save property public page:', error);
      toastService.error(error instanceof Error ? error.message : t('common.errorGeneric'));
    } finally {
      this.saving = false;
      this.render();
    }
  }

  private async copyLink(): Promise<void> {
    const url = this.publicPage?.publicUrl;
    if (!url) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const input = document.createElement('input');
        input.value = url;
        input.style.position = 'fixed';
        input.style.left = '-9999px';
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        input.remove();
      }

      this.copyFeedback = t('property.publicPage.copySuccess');
      toastService.success(this.copyFeedback);
    } catch {
      this.copyFeedback = t('property.publicPage.copyError');
      toastService.error(this.copyFeedback);
    }

    this.render();
  }

  private setDraft(publicPage: PropertyPublicPageDto): void {
    this.draftIsActive = publicPage.isActive;
    this.draftExpiresAt = toLocalDateTimeInput(publicPage.expiresAtUtc);
  }

  private shouldLoadData(): boolean {
    return Boolean(this.propertyId) && !this.hasAttribute('hidden');
  }

  private getStatus(): { label: string; badgeClass: string } {
    if (!this.publicPage?.isConfigured) {
      return { label: t('property.publicPage.status.notConfigured'), badgeClass: 'badge-muted' };
    }

    if (this.publicPage.expiresAtUtc && new Date(this.publicPage.expiresAtUtc).getTime() <= Date.now()) {
      return { label: t('property.publicPage.status.expired'), badgeClass: 'badge-warning' };
    }

    if (this.publicPage.isActive) {
      return { label: t('property.publicPage.status.active'), badgeClass: 'badge-success' };
    }

    return { label: t('property.publicPage.status.inactive'), badgeClass: 'badge-muted' };
  }
}

if (!customElements.get('rc-property-public-page-tab')) {
  customElements.define('rc-property-public-page-tab', RcPropertyPublicPageTab);
}
